import { createFileRoute } from "@tanstack/react-router";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { EnvPreset } from "gfx/EnvPreset";
import { SITE_LAYER } from "gfx/layerConstants";
import { designMaterialNew } from "gfx/materials/designMaterial";
import { cloneDefaultMaterialParams } from "gfx/materials/designMaterialUtils";
import { envPresetSceneLights } from "gfx/sceneLights";
import type { SHLightProbeGrid } from "gfx/threejs/higharc/SHLightProbeGrid";
import THREEViewer from "gfx/THREEViewer";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import withAuthentication from "../../components/withAuthentication";

function SsrrTestPage() {
  const viewerContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<THREEViewer | undefined>(undefined);
  const lightProbeGridRef = useRef<SHLightProbeGrid | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      const sceneLights = envPresetSceneLights[EnvPreset.MORNING];

      const viewer = (viewerRef.current = new THREEViewer({
        container: viewerContainerRef.current,
        options: {
          gui: true,
          ssfxRenderer: true,
          animate: true,
          sceneLights,
          debugFeaturesEnabled: true,
          cameraSettings: {
            name: "Default",
            fov: 50,
            ortho: false,
            x: -150,
            y: 150,
            z: 150,
            targetX: 0,
            targetY: 0,
            targetZ: 0,
            upX: 0,
            upY: 0,
            upZ: 1,
            cameraNear: 5,
            cameraFar: 10000,
          },
        },
      }));

      await viewer.guiPromise;

      if (!viewerRef.current) {
        return;
      }

      viewer.updateSceneRenderParams({ renderPipelineParams: { reflectionsEnabled: true } });

      const viewerRenderer = viewer.viewerRenderer;

      const mirrorGeom = new THREE.PlaneGeometry(400, 400);

      const materialParams = cloneDefaultMaterialParams();
      materialParams.metalness = 1.0;
      materialParams.roughness = 0.1;
      const reflectiveMat = await designMaterialNew(
        materialParams,
        viewerRenderer.renderer,
        (_url, _overrideFormat) => [null, new Promise((resolve, _reject) => resolve())],
        viewer.hasSSFXRenderer,
        undefined,
      );

      if (!viewerRef.current) {
        return;
      }

      const groundMesh = new THREE.Mesh(mirrorGeom, reflectiveMat);
      viewer.scene.add(groundMesh);

      const mirrorMesh = new THREE.Mesh(mirrorGeom, reflectiveMat);
      viewer.scene.add(mirrorMesh);
      mirrorMesh.setRotationFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI * 0.3);
      mirrorMesh.scale.set(0.3, 0.3, 0.3);

      const cubeGeom = new THREE.BoxGeometry(20, 20, 20);

      const cube = new THREE.Mesh(cubeGeom, new THREE.MeshStandardMaterial());
      cube.position.set(0, 0, 10);
      viewer.scene.add(cube);

      await initLightProbe();
    })();

    return () => {
      if (lightProbeGridRef.current) {
        const lightProbeGrid = lightProbeGridRef.current;
        lightProbeGrid.dispose();
      }
      if (viewerRef.current) {
        const viewer = viewerRef.current;
        viewer.scene.clear();
        viewer.dispose();
        viewerRef.current = undefined;
      }
    };
  }, []);

  async function initLightProbe() {
    const viewer = viewerRef.current;
    assertIsNotUndefined(viewer);
    const services = viewer.viewerRenderer.services;

    // Generate a single light probe for the site. It will only have the site layer rendered into it.
    const lightProbeGrid = (lightProbeGridRef.current = await services.createLightProbeGrid(1, 1, 1, "site"));
    lightProbeGrid.renderLayers.set(SITE_LAYER);
    lightProbeGrid.position.set(0, 0, 100);
    lightProbeGrid.scale.set(250, 250, 250);
    lightProbeGrid.updateMatrix();
    lightProbeGrid.updateMatrixWorld();
    lightProbeGrid.layers.enableAll();
    lightProbeGrid.probesNeedUpdate = true;
    viewer.scene.staticContent.add(lightProbeGrid);
    viewer.scene.updateLightProbesList();
  }

  return (
    <div>
      <Head>
        <title>{getPageTitle("SSRR Test")}</title>
        <meta name="robots" content="noindex" />

        <style>{`
          body {
              width:  100%;
              height: 100%;
              margin: 0;
              padding: 0;
          }

          .viewerContainer {  overflow: hidden; position: absolute; left: 0; right: 0; top: 0; bottom: 0; }
        `}</style>
      </Head>

      <div className="viewerContainer" ref={viewerContainerRef} />
    </div>
  );
}

export const Route = createFileRoute("/debug/ssrr-test")({
  component: withAuthentication(SsrrTestPage, { superAdmin: true }),
});
